import { css, Theme, Interpolation } from '@emotion/react'
import { useEffect, useState } from 'react'
import ColorThief, { Palette } from 'colorthief/dist/color-thief.mjs'
import { QuestionInvertTypeEnum } from '__generatedTypes__/globalTypes'

export const PROXY_URL = process.env.REACT_APP_NEXT_APP_ORIGIN + '/api/getImage?url='
export const DATA_ATTRIBUTE = 'data-invert-for-dark-mode'

const colorThief = new ColorThief()

const getIsMonochrome = async (img: HTMLImageElement): Promise<boolean | undefined> => {
  try {
    const color: Palette = await new Promise((resolve) => {
      ;(async () => {
        if (img.complete) {
          const color = await colorThief.getColor(img)
          resolve(color)
        } else {
          img.addEventListener('load', async () => {
            const color = await colorThief.getColor(img)
            resolve(color)
          })
        }
      })()
    })
    return color[0] === color[1] && color[0] === color[2] && color[1] === color[2]
  } catch (error) {
    return undefined
  }
}

type ImageInversionResult = {
  imageUrl: string
  dataInvertForDarkMode: QuestionInvertTypeEnum.YES | QuestionInvertTypeEnum.NO
  analysisSucceeded: boolean
}

// Process a single image URL to determine inversion setting
// Returns the URL to use (proxy or original) and the inversion attribute
const processImageForInversion = async (
  imageUrl: string,
  invertImagesForDarkMode?: QuestionInvertTypeEnum
): Promise<ImageInversionResult> => {
  const isBase64 = imageUrl.startsWith('data:')

  // For explicit YES/NO modes, use original URL without analysis
  if (invertImagesForDarkMode && invertImagesForDarkMode !== QuestionInvertTypeEnum.AUTO) {
    return {
      imageUrl: isBase64 ? imageUrl : PROXY_URL + encodeURIComponent(imageUrl),
      dataInvertForDarkMode: invertImagesForDarkMode,
      analysisSucceeded: true,
    }
  }

  // For AUTO mode, load and analyze (works for both base64 and external URLs)
  return new Promise((resolve) => {
    const proxyUrl = isBase64 ? imageUrl : PROXY_URL + encodeURIComponent(imageUrl)
    const image = new Image()
    if (!isBase64) {
      image.crossOrigin = 'Anonymous'
    }

    image.onload = async function () {
      const isMonochrome = await getIsMonochrome(image)

      if (isMonochrome === undefined) {
        // Analysis failed - use proxy URL but don't invert
        resolve({
          imageUrl: proxyUrl,
          dataInvertForDarkMode: QuestionInvertTypeEnum.NO,
          analysisSucceeded: false,
        })
      } else {
        // Analysis succeeded - use proxy URL
        resolve({
          imageUrl: proxyUrl,
          dataInvertForDarkMode: isMonochrome ? QuestionInvertTypeEnum.YES : QuestionInvertTypeEnum.NO,
          analysisSucceeded: true,
        })
      }
    }

    image.onerror = () => {
      // Load failed - use proxy URL but don't invert
      resolve({
        imageUrl: proxyUrl,
        dataInvertForDarkMode: QuestionInvertTypeEnum.NO,
        analysisSucceeded: false,
      })
    }

    image.src = proxyUrl
  })
}

export type StylesParams = {
  background?: string
  isDarkAlways?: boolean
}

export const getInvertStyles: (params?: StylesParams) => Interpolation<Theme> = (params) => (theme) =>
  params?.isDarkAlways &&
  css`
    img[${DATA_ATTRIBUTE}='${QuestionInvertTypeEnum.YES}'] {
      filter: invert(100%);
    }

    img[${DATA_ATTRIBUTE}='${QuestionInvertTypeEnum.NO}'] {
      background: ${params?.background ?? theme.colors.white};
    }
  `

export const replaceImageSrcWithProxy = (html: string) =>
  html.replace(/<img.*?src="(.*?)".*?>/gi, function (match, src) {
    // Don't proxy base64 images
    if (src.startsWith('data:')) {
      return match
    }
    return match.replace(src, PROXY_URL + encodeURIComponent(src))
  })

// Find all img tags with src attribute
const findAllImages = (html: string) =>
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  [...html.matchAll(/<img.*?src="(.*?)".*?>/gi)].map(([match, imageUrl]) => ({ match, imageUrl }))

// Find all spans with data-preview-image attribute (Tooltip Image Preview feature)
const findAllPreviewImageLinks = (html: string) =>
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  [...html.matchAll(/<span.*?data-preview-image="(.*?)".*?>/gi)].map(([match, imageUrl]) => ({ match, imageUrl }))

// Replace img tags with proxy URL and inversion data attribute
const replaceImages = (
  html: string,
  imagesData: Array<{
    match: string
    imageUrl: string
    dataInvertForDarkMode: QuestionInvertTypeEnum.YES | QuestionInvertTypeEnum.NO
  }>
) =>
  imagesData.reduce((html, { match, imageUrl, dataInvertForDarkMode }) => {
    // Replace original src with proxy URL and add data attribute
    const updatedMatch = match.replace(/src=".*?"/, `src="${imageUrl}" ${DATA_ATTRIBUTE}="${dataInvertForDarkMode}"`)
    return html.replace(match, updatedMatch)
  }, html)

// Replace preview-image-link spans with inversion data attribute
const replacePreviewImageLinks = (
  html: string,
  linksData: Array<{
    match: string
    dataInvertForDarkMode: QuestionInvertTypeEnum.YES | QuestionInvertTypeEnum.NO
  }>
) =>
  linksData.reduce((html, { match, dataInvertForDarkMode }) => {
    // Insert the data attribute before the closing >
    const updatedMatch = match.replace(/>$/, ` ${DATA_ATTRIBUTE}="${dataInvertForDarkMode}">`)
    return html.replace(match, updatedMatch)
  }, html)

export const prepareHtmlWithImages = async (html: string, invertImagesForDarkMode?: QuestionInvertTypeEnum) => {
  let processedHtml = html

  // Process regular img tags
  const images = findAllImages(html)

  if (images.length) {
    const imagesUpd = await Promise.all(
      images.map(async ({ match, imageUrl: originalUrl }) => {
        const { imageUrl, dataInvertForDarkMode } = await processImageForInversion(originalUrl, invertImagesForDarkMode)

        return { match, imageUrl, dataInvertForDarkMode }
      })
    )

    processedHtml = replaceImages(processedHtml, imagesUpd)
  }

  // Process preview-image-link spans (only need inversion attribute, URL stays the same)
  const previewImageLinks = findAllPreviewImageLinks(processedHtml)

  if (previewImageLinks.length) {
    const linksUpd = await Promise.all(
      previewImageLinks.map(async ({ match, imageUrl }) => {
        const { dataInvertForDarkMode } = await processImageForInversion(imageUrl, invertImagesForDarkMode)

        return { match, dataInvertForDarkMode }
      })
    )

    processedHtml = replacePreviewImageLinks(processedHtml, linksUpd)
  }

  return processedHtml
}

export const useInvertImages = (html: string, invertImagesForDarkMode?: QuestionInvertTypeEnum) => {
  const [htmlWithUpdatedImages, setHtmlWithUpdatedImages] = useState(replaceImageSrcWithProxy(html))
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setHtmlWithUpdatedImages(replaceImageSrcWithProxy(html))
    setIsLoading(true)
    ;(async () => {
      const htmlUpd = await prepareHtmlWithImages(html, invertImagesForDarkMode)
      setHtmlWithUpdatedImages(htmlUpd)
      setIsLoading(false)
    })()
  }, [html, invertImagesForDarkMode])

  return {
    htmlUpdated: htmlWithUpdatedImages,
    isLoading,
  }
}
